import React, { useCallback, useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import {
    SubNavigationPortalContext,
    SubNavigationPortalContextValue,
} from "./SubNavigationPortal";

interface SubNavigationPortalProviderProps {
    children: React.ReactNode;
    targetSelector?: string;
}

export const SubNavigationPortalProvider: React.FC<
    SubNavigationPortalProviderProps
> = ({ children, targetSelector = "[data-sub-navigation]" }) => {
    const [targetElement, setTargetElement] = useState<HTMLElement | null>(
        null,
    );
    const [portals, setPortals] = useState<Map<string, React.ReactNode>>(
        new Map(),
    );

    useEffect(() => {
        const element = document.querySelector(targetSelector) as HTMLElement;
        if (element) {
            setTargetElement(element);
        }
    }, [targetSelector]);

    const registerPortal = useCallback(
        (id: string, content: React.ReactNode) => {
            setPortals((prev) => new Map(prev).set(id, content));
        },
        [],
    );

    const unregisterPortal = useCallback((id: string) => {
        setPortals((prev) => {
            const newPortals = new Map(prev);
            newPortals.delete(id);
            return newPortals;
        });
    }, []);

    const contextValue = useMemo<SubNavigationPortalContextValue>(
        () => ({
            registerPortal,
            unregisterPortal,
            targetElement,
        }),
        [registerPortal, unregisterPortal, targetElement],
    );

    return (
        <SubNavigationPortalContext.Provider value={contextValue}>
            {children}
            {targetElement &&
                portals.size > 0 &&
                createPortal(
                    <div className="sub-navigation-portals">
                        {Array.from(portals.entries()).map(([id, content]) => (
                            <div key={id} data-portal-id={id}>
                                {content}
                            </div>
                        ))}
                    </div>,
                    targetElement,
                )}
        </SubNavigationPortalContext.Provider>
    );
};
